import { useState, useEffect } from 'react';
import { Package, Truck, CheckCircle, XCircle, Clock, CreditCard, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { orderAPI } from '../utils/api';
import { getProductImageSrc } from '../utils/images';
import { Order } from '../types';
import toast from 'react-hot-toast';

export const Orders = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log('Fetching orders for userId:', user.id);
        const response = await orderAPI.getOrders(user.id);
        console.log('Orders response:', response);
        // Map API response to match UI expectations
        const mappedOrders = (response.orders || []).map((order: any) => ({
          orderId: order.orderNumber,
          id: order.id,
          userId: order.userId,
          status: order.status.toLowerCase(),
          paymentStatus: order.paymentDetails?.status || 'pending',
          items: order.items.map((item: any) => ({
            productId: item.productId,
            productName: item.productName,
            productImage: `product_${item.productId}.png`,
            quantity: item.quantity,
            size: item.sku || 'N/A',
            price: item.price,
            total: item.totalPrice
          })),
          subtotal: order.subtotal,
          discount: 0, // Not provided in current API
          shipping: order.shipping,
          tax: order.tax,
          total: order.total,
          shippingAddress: {
            id: 'shipping',
            type: 'home' as const,
            street: order.shippingAddress?.street || 'N/A',
            city: order.shippingAddress?.city || 'N/A',
            state: order.shippingAddress?.state || 'N/A',
            zipCode: order.shippingAddress?.zipCode || 'N/A',
            country: order.shippingAddress?.country || 'N/A',
            isDefault: true
          },
          billingAddress: order.billingAddress ? {
            id: 'billing',
            type: 'home' as const,
            street: order.billingAddress.street,
            city: order.billingAddress.city,
            state: order.billingAddress.state,
            zipCode: order.billingAddress.zipCode,
            country: order.billingAddress.country,
            isDefault: false
          } : undefined,
          paymentMethod: order.paymentDetails?.paymentMethod || order.paymentMethod || 'card',
          paymentDetails: order.paymentDetails,
          estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          statusHistory: [{
            status: order.status.toLowerCase() as any,
            timestamp: order.statusUpdatedAt || order.updatedAt,
            message: `Order ${order.status.toLowerCase()}`
          }],
          createdAt: order.createdAt,
          updatedAt: order.updatedAt,
          notes: order.notes
        }));
        setOrders(mappedOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
        // Error is handled by the API interceptor
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, user]);

  const getPaymentStatusBadge = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
      case 'confirmed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Paid</span>;
      case 'pending':
      case 'initiated':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
      case 'failed':
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Failed</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Pending</span>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-blue-500" />;
      case 'confirmed':
        return <Package className="h-5 w-5 text-blue-500" />;
      case 'payment_failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'text-green-600 bg-green-100';
      case 'shipped':
        return 'text-blue-600 bg-blue-100';
      case 'confirmed':
        return 'text-blue-600 bg-blue-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusIcon = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <CreditCard className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const generateAmazonStyleInvoice = (order: Order) => {
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatCurrency = (amount: number) => {
      return `₹${amount.toFixed(2)}`;
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Order Invoice - ${order.orderId}</title>
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none !important; }
          }
          
          body { 
            font-family: 'Amazon Ember', Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
            color: #0F1111; 
            background: #fff;
            line-height: 1.4;
          }
          
          .container { max-width: 800px; margin: 0 auto; }
          
          .header { 
            border-bottom: 1px solid #D5D9D9; 
            padding-bottom: 20px; 
            margin-bottom: 20px; 
          }
          
          .logo { 
            font-size: 28px; 
            font-weight: 400; 
            color: #232F3E; 
            margin-bottom: 5px;
            letter-spacing: -0.5px;
          }
          
          .order-title { 
            font-size: 18px; 
            color: #0F1111; 
            font-weight: 400;
            margin: 0;
          }
          
          .order-info { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 20px; 
            flex-wrap: wrap;
          }
          
          .info-section { 
            flex: 1; 
            min-width: 200px;
            margin-bottom: 15px;
          }
          
          .info-section h3 { 
            font-size: 14px; 
            font-weight: 700; 
            color: #0F1111; 
            margin: 0 0 8px 0; 
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .info-section p { 
            margin: 3px 0; 
            font-size: 13px;
            color: #565959;
          }
          
          .address-section {
            background: #F7F7F7;
            padding: 15px;
            border-radius: 4px;
            margin-bottom: 20px;
          }
          
          .items-section {
            margin-bottom: 20px;
          }
          
          .item { 
            display: flex; 
            padding: 15px 0; 
            border-bottom: 1px solid #E7E7E7; 
            align-items: flex-start;
          }
          
          .item:last-child { border-bottom: none; }
          
          .item-image { 
            width: 60px; 
            height: 60px; 
            background: #F3F3F3; 
            border-radius: 4px; 
            margin-right: 15px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #888;
          }
          
          .item-details { flex: 1; }
          
          .item-name { 
            font-size: 14px; 
            font-weight: 400; 
            color: #0F1111; 
            margin-bottom: 5px;
            line-height: 1.3;
          }
          
          .item-meta { 
            font-size: 12px; 
            color: #565959; 
            margin-bottom: 8px;
          }
          
          .item-price { 
            font-size: 14px; 
            font-weight: 700; 
            color: #0F1111;
          }
          
          .summary-section {
            background: #F7F7F7;
            padding: 20px;
            border-radius: 4px;
            margin-top: 20px;
          }
          
          .summary-row { 
            display: flex; 
            justify-content: space-between; 
            padding: 8px 0; 
            font-size: 14px;
          }
          
          .summary-row.total { 
            border-top: 1px solid #D5D9D9; 
            font-weight: 700; 
            font-size: 16px;
            margin-top: 10px;
            padding-top: 15px;
          }
          
          .footer { 
            margin-top: 30px; 
            text-align: center; 
            color: #565959; 
            font-size: 12px;
            border-top: 1px solid #E7E7E7;
            padding-top: 20px;
          }
          
          .status-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .status-pending { background: #FFF3CD; color: #856404; }
          .status-confirmed { background: #D1ECF1; color: #0C5460; }
          .status-shipped { background: #D4EDDA; color: #155724; }
          .status-delivered { background: #D1ECF1; color: #0C5460; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">NINEEYE</div>
            <h1 class="order-title">Order Invoice</h1>
          </div>

          <div class="order-info">
            <div class="info-section">
              <h3>Order Information</h3>
              <p><strong>Order #:</strong> ${order.orderId}</p>
              <p><strong>Order Date:</strong> ${formatDate(order.createdAt)}</p>
              <p><strong>Status:</strong> <span class="status-badge status-${order.status}">${order.status}</span></p>
              <p><strong>Payment:</strong> ${order.paymentStatus?.toUpperCase() || 'PENDING'}</p>
            </div>
            <div class="info-section">
              <h3>Payment Method</h3>
              <p>${order.paymentMethod === 'payglocal' ? 'Online Payment (PayGlocal)' : order.paymentMethod.toUpperCase()}</p>
              <p><strong>Status:</strong> ${order.paymentStatus || 'Pending'}</p>
              ${order.paymentDetails?.transactionId ? `<p><strong>Transaction ID:</strong> ${order.paymentDetails.transactionId}</p>` : ''}
            </div>
          </div>

          <div class="address-section">
            <h3>Shipping Address</h3>
            <p>${order.shippingAddress.street}</p>
            <p>${order.shippingAddress.city}, ${order.shippingAddress.state} ${order.shippingAddress.zipCode}</p>
            <p>${order.shippingAddress.country}</p>
          </div>

          <div class="items-section">
            <h3>Order Items</h3>
            ${order.items.map(item => `
              <div class="item">
                <div class="item-image">IMG</div>
                <div class="item-details">
                  <div class="item-name">${item.productName}</div>
                  <div class="item-meta">Size: ${item.size} • Quantity: ${item.quantity}</div>
                  <div class="item-price">${formatCurrency(item.total)}</div>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="summary-section">
            <div class="summary-row">
              <span>Items Subtotal:</span>
              <span>${formatCurrency(order.subtotal)}</span>
            </div>
            <div class="summary-row">
              <span>Shipping:</span>
              <span>${formatCurrency(order.shipping)}</span>
            </div>
            <div class="summary-row">
              <span>Tax:</span>
              <span>${formatCurrency(order.tax)}</span>
            </div>
            <div class="summary-row total">
              <span>Order Total:</span>
              <span>${formatCurrency(order.total)}</span>
            </div>
          </div>

          <div class="footer">
            <p>Thank you for shopping with NINEEYE!</p>
            <p>For any questions about this order, please contact our customer service.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const toggleOrderExpansion = (orderId: string) => {
    const newExpandedOrders = new Set(expandedOrders);
    if (newExpandedOrders.has(orderId)) {
      newExpandedOrders.delete(orderId);
    } else {
      newExpandedOrders.add(orderId);
    }
    setExpandedOrders(newExpandedOrders);
  };

  const handleDownloadInvoice = (order: Order) => {
    try {
      const invoiceHTML = generateAmazonStyleInvoice(order);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();
        
        // Wait for content to load, then trigger print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Close the window after printing
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }, 500);
        };
      }
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  // Show login prompt for non-authenticated users
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-8">Please login to view your orders</p>
          <button className="btn-primary btn-lg">Login</button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
            <button className="btn-primary btn-md">Start Shopping</button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const isExpanded = expandedOrders.has(order.orderId);
              return (
                <div key={order.orderId} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Order Header - Always Visible */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleOrderExpansion(order.orderId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(order.status)}
                          <span className="font-semibold text-gray-900">Order #{order.orderId}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize flex items-center space-x-1 ${getPaymentStatusColor(order.paymentStatus || 'pending')}`}>
                            {getPaymentStatusIcon(order.paymentStatus || 'pending')}
                            <span>{order.paymentStatus || 'pending'}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">₹{order.total.toFixed(2)}</p>
                          <p className="text-sm text-gray-500">{order.items.length} items</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details - Only visible when expanded */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-6 space-y-6">
                      {/* Order Summary */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Order Date</p>
                          <p className="font-medium text-gray-900">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Method</p>
                          <p className="font-medium text-gray-900 capitalize">
                            {order.paymentMethod === 'payglocal' ? 'Online Payment' : order.paymentMethod}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Status</p>
                          <div className="mt-1">
                            {getPaymentStatusBadge(order.paymentStatus)}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Estimated Delivery</p>
                          <p className="font-medium text-gray-900">
                            {new Date(order.estimatedDelivery).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      {order.shippingAddress && (
                        <div>
                          <p className="text-sm text-gray-500 mb-2">Shipping Address</p>
                          <p className="text-sm text-gray-900">
                            {order.shippingAddress.street}, {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}, {order.shippingAddress.country}
                          </p>
                        </div>
                      )}

                      {/* Order Items */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Order Items</h4>
                        <div className="space-y-3">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <img
                                src={getProductImageSrc(item.productImage)}
                                alt={item.productName}
                                className="w-12 h-12 object-cover rounded"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/images/placeholder.png';
                                }}
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                                <p className="text-sm text-gray-500">Size: {item.size} • Qty: {item.quantity}</p>
                              </div>
                              <p className="text-sm font-medium text-gray-900">₹{item.total.toFixed(2)}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Order Summary */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Subtotal:</span>
                          <span className="text-sm font-medium">₹{order.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Shipping:</span>
                          <span className="text-sm font-medium">₹{order.shipping.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Tax:</span>
                          <span className="text-sm font-medium">₹{order.tax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="font-semibold text-gray-900">Total:</span>
                          <span className="font-semibold text-gray-900">₹{order.total.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-4">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadInvoice(order);
                          }}
                          className="btn-outline btn-sm flex items-center space-x-2"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download Invoice</span>
                        </button>
                        {order.status === 'delivered' && (
                          <button className="btn-primary btn-sm">Reorder</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
